import { IAlertProps } from "@components/alert/Alert.types";
import {
    EditableAmountDialog,
    IEditableAmountDialogData
} from "@components/smart/selectionTableView/EditableAmountDialog";
import { ISelectionWithEditableAmountCustomTableData } from "@components/smart/selectionTableView/SelectionTableView.utils";
import { IRowProps } from "@components/table/Rows.types";
import BindingContext, { IEntity } from "@odata/BindingContext";
import { getValue } from "@utils/general";
import { memoizeOne } from "@utils/memoizeOne";
import React, { ComponentType } from "react";

import { ActionState, IconSize, RowAction, Status } from "../../../enums";
import { EmptyObject } from "../../../global.types";
import { ModelEvent } from "../../../model/Model";
import TableView from "../../../views/table";
import {
    TableActionOrder,
    TableButtonsAction,
    TableButtonsActionType,
    TTableToolbarItem
} from "../../../views/table/TableToolbar.utils";
import { IHandleCustomActionArgs, ITableViewBaseProps } from "../../../views/table/TableView";
import { getConfirmationActionText } from "../../../views/table/TableView.render.utils";
import { TableWrapper } from "../../../views/table/TableView.styles";
import { createCustomTableToolbarItem, getTableViewMessage } from "../../../views/table/TableView.utils";
import View from "../../../views/View";
import { WithBusyIndicator } from "../../busyIndicator/withBusyIndicator";
import { IconButton } from "../../button";
import { getIcon, IProps as IIconProps } from "../../icon";
import ReadOnlyList from "../../readOnlyList/ReadOnlyList";
import { TitleStyled } from "../../readOnlyList/ReadOnlyList.styles";
import { IReadOnlyListItem } from "../../readOnlyList/ReadOnlyListItem";
import { IActionRendererArgs, IRow, IRowAction, TId } from "../../table";
import { IProps as ISmartFilterBarProps } from "../smartFilterBar/SmartFilterBar";
import { SmartTable } from "../smartTable";
import { TSmartODataTableStorage } from "../smartTable/SmartODataTableBase";
import { getRow, TCustomRowAction } from "../smartTable/SmartTable.utils";

export const SELECTION_TABLEVIEW_ACTION = "selectionTableViewAction";

export type THeaderDataGetter = IReadOnlyListItem[] | (() => IReadOnlyListItem[]);

export interface ISelectionTableViewProps extends ITableViewBaseProps {
    selectedItems?: BindingContext[];
    onClose?: () => void;
    onSelection?: (selected: BindingContext[], entities: IEntity[], storage?: TSmartODataTableStorage) => Promise<boolean | void>;

    title?: string;
    secondaryTitle?: string;
    headerData?: THeaderDataGetter;
    confirmText?: string;
    successMessage?: string;
    isRowWithoutAction?: (storage: TSmartODataTableStorage, rowId: TId, action: RowAction, row: IRow) => boolean;
    presetDefaultFilters?: (storage: TSmartODataTableStorage) => void;
    /** Whether only one row can be selected (paired), or multiple, true by default. */
    isSingleSelect?: boolean;
    // for multi select variant, if we don't want to render the main toggle
    withoutMainToggle?: boolean;
}

export interface IProps extends ISelectionTableViewProps, WithBusyIndicator {
}

export default abstract class SelectionTableViewBase<Props extends IProps = IProps, State = EmptyObject> extends TableView<Props, State> {
    // configuration options
    _actionIconName = "CopyLink";
    _selectedIconName = "Chained";
    _notSelectedIconName = "Unchained";
    _hotspotContextId: string = null;

    // common props
    _selectedItems: BindingContext[];
    _keepActiveRowsOnFilterChange = true;

    // ReadOnlyList could be customized in extended classes or passed by props
    get title(): string {
        return this.props.title;
    }

    get headerData(): THeaderDataGetter {
        return this.props.headerData;
    }

    get secondaryTitle(): string {
        return this.props.secondaryTitle;
    }

    get selectionConfirmText(): string {
        return this.props.confirmText;
    }

    get successMessage(): string {
        return this.props.successMessage;
    }

    get isSingleSelect(): boolean {
        return this.props.isSingleSelect ?? true;
    }

    get actionIconName(): string {
        return this._actionIconName;
    }

    get tNamespace(): string {
        return null;
    }

    constructor(props: IProps) {
        // @ts-ignore
        super(props);

        this.handleCustomAction = this.handleCustomAction.bind(this);
        this.handleSelection = this.handleSelection.bind(this);
        this.renderBusyIndicator = this.renderBusyIndicator.bind(this);
        this.presetDefaultFilters = this.presetDefaultFilters.bind(this);

        this._selectedItems = props.selectedItems;
        this.handleCustomAction(SELECTION_TABLEVIEW_ACTION, { update: false });

        props.storage.emitter.on(ModelEvent.FiltersCleared, this.presetDefaultFilters);
        this.presetDefaultFilters();
    }

    shouldComponentUpdate(nextProps: Readonly<Props>, nextState: Readonly<State>): boolean {
        return super.shouldComponentUpdate(nextProps, nextState) || this.props.busy !== nextProps.busy;
    }

    componentWillUnmount() {
        super.componentWillUnmount();

        this.props.storage.emitter.off(ModelEvent.FiltersCleared, this.presetDefaultFilters);
    }

    presetDefaultFilters(): void {
        // we can customize default filters for the tableView, which
        // should be kept even when user clears filters. They can be only removed manually or changed.
        if (this.props.presetDefaultFilters) {
            this.props.presetDefaultFilters(this.props.storage);
        }
    }

    getCustomFilterBarProps(): Partial<ISmartFilterBarProps> {
        return {
            onFilterCustomizationChanged: this.presetDefaultFilters
        };
    }

    getToolbarButtons(): TableButtonsActionType[] {
        return [
            // https://solitea-cz.atlassian.net/browse/DEV-24786
            // apparently, users don't want to see the table settings
            // TableButtonsAction.Settings
        ];
    }

    get storage(): TSmartODataTableStorage<ISelectionWithEditableAmountCustomTableData> {
        return this.props.storage as TSmartODataTableStorage<ISelectionWithEditableAmountCustomTableData>;
    }

    get customToolbarItems(): TTableToolbarItem[] {
        return [this.createToolbarItem(SELECTION_TABLEVIEW_ACTION, this.actionIconName, TableActionOrder.Add, this.selectionConfirmText)];
    }

    createToolbarItem(id: string, iconName: string, order: number, label: string, forceDisabled = false): TTableToolbarItem {
        const action = this.getTableAction();

        return createCustomTableToolbarItem({
            tableAction: action,
            id,
            iconName,
            label,
            forceDisabled,
            order
        });
    }

    getRow = (bindingContext: BindingContext): IRow => {
        return getRow(this.props.storage.tableAPI.getState().rows, bindingContext);
    };

    getConfirmText = (tableAction: TableButtonsAction | string): string | React.ReactElement => {
        const { storage } = this.props;

        if (tableAction === SELECTION_TABLEVIEW_ACTION) {
            if (this.isSingleSelect) {
                return this.selectionConfirmText;
            } else {
                const activeRowActionCount = storage.tableAPI?.getState().activeRows.size ?? 0;
                return getConfirmationActionText(this.selectionConfirmText, activeRowActionCount);
            }
        }

        return super.getConfirmText(tableAction);
    };

    handleCustomAction(action: string, { update = true, value }: IHandleCustomActionArgs = {}): void {
        this.setTableAction(action, true, false);
        if (update) {
            this.forceUpdate();
        }
    }

    getIcon = (isActive: boolean): ComponentType<IIconProps> => isActive ? getIcon(this._selectedIconName) : getIcon(this._notSelectedIconName);

    isRowWithoutAction(rowId: TId, action: RowAction, row: IRow): boolean {
        if (!this.props.isRowWithoutAction) {
            return false;
        }
        return this.props.isRowWithoutAction?.(this.props.storage, rowId, action, row);
    }

    handleRowActionClick: ((rowId: TId, row?: IRowProps) => void) = undefined; // to be overridden in extended classes

    getCustomRowAction = memoizeOne((): TCustomRowAction => {
        return {
            actionType: RowAction.Custom,
            isSingleSelect: this.isSingleSelect,
            onClick: this.handleRowActionClick,
            render: (args: IActionRendererArgs) => {
                if (args.isMainToggle && (this.props.withoutMainToggle || this.isSingleSelect)) {
                    return null;
                }

                const isActive = args.actionState === ActionState.Active;
                const Icon = this.getIcon(isActive);
                return (
                    <IconButton title=""
                                onClick={args.onClick}
                                isDisabled={args.actionState === ActionState.None || args.isDisabled}
                                isDecorative>
                        <Icon width={IconSize.S} height={IconSize.S}/>
                    </IconButton>
                );
            }
        };
    });

    setAlert({ status, subTitle, ...restProps }: IAlertProps): void {
        this.props.storage.data.alert = {
            ...getTableViewMessage(status, subTitle),
            ...restProps
        };
    }

    getRowIcon = (id: TId, row: IRowProps, rowAction: IRowAction): React.ComponentType<IIconProps> => {
        if (rowAction) {
            return null;
        }

        // this._selectedItems has corrected entity type, different from the one in id
        // (e.g. Documents vs InvoicesIssued)
        // but documents have unique id across all the types
        // => use just key
        return this._selectedItems?.map(item => item.getKey()).includes((id as BindingContext).getKey()) && this.getIcon(true);
    };

    // can be overridden in extended tableview
    getCorrectBindingContext = (bindingContext: BindingContext, row: IRow): BindingContext => {
        return bindingContext;
    };

    // can be overridden in extended tableview
    getEditableAmountDialogData(row: IRow, pairedDialogItemId: string): IEditableAmountDialogData {
        return null;
    }

    handleToolbarConfirm = async (): Promise<void> => {
        const { storage } = this.props;

        this.props.setBusy(true);
        const affectedRows = await storage.tableAPI.getAffectedRows();

        // contains the original bcs
        this._selectedItems = affectedRows?.map(affectedRow => {
            return affectedRow.bc;
        });
        // contains the corrected bcs
        const affectedBCs = affectedRows?.map(affectedRow => {
            const bc = affectedRow.bc;
            const row = bc && this.getRow(bc);

            return this.getCorrectBindingContext(bc, row);
        });
        const entities = affectedRows.map(row => this.getRow(row.bc).customData?.entity);

        const ret = await this.handleSelection?.(affectedBCs, entities, storage);
        await this.setTableAction(null);
        this.props.setBusy(false);
        this.props.storage.tableAPI?.reloadTable();

        // if success message is configured and at least one entity selected, shows success message
        if (this.successMessage && entities?.[0] && ret !== false) {
            this.setAlert({
                status: Status.Success,
                subTitle: this.successMessage
            });
        }
    };

    handleSelection(selected: BindingContext[], entities: IEntity[], storage?: TSmartODataTableStorage): Promise<boolean | void> {
        return this.props.onSelection(selected, entities, storage);
    }

    canConfirmToolbarAction(): boolean {
        if (this.props.storage.data.tableAction === SELECTION_TABLEVIEW_ACTION) {
            return true;
        }
        return super.canConfirmToolbarAction();
    }

    handlePairAmountChange = (transactionAmount: number): void => {
        const { pairedItems, pairedDialogItemId } = this.storage.getCustomData();
        pairedItems[pairedDialogItemId] = {
            ...pairedItems[pairedDialogItemId],
            TransactionAmount: transactionAmount,
            Amount: transactionAmount * (pairedItems[pairedDialogItemId]?.ExchangeRatePerUnit ?? 1)
        };
        this.storage.setCustomData({ pairedItems: { ...(pairedItems ?? {}) } });
    };

    handleCloseDialog = (): void => {
        this.storage.setCustomData({ pairedDialogItemId: null });
        this.storage.refresh();
    };

    handleConfirmDialog = (amount: number): void => {
        this.handlePairAmountChange(amount);
        this.handleCloseDialog();
    };

    handleRowSelect = (bindingContext: BindingContext): void => {
        const correctedBindingContext = this.getCorrectBindingContext(bindingContext, this.getRow(bindingContext));
        this.props.onRowSelect?.(correctedBindingContext);
    };

    renderDefaultDialogs(): React.ReactElement {
        const { pairedDialogItemId } = this.storage.getCustomData();
        if (pairedDialogItemId) {
            const row = this.getRow(this.storage.data.bindingContext.createNewBindingContext(pairedDialogItemId));
            const data: IEditableAmountDialogData = this.getEditableAmountDialogData(row, pairedDialogItemId);
            return (
                    <EditableAmountDialog data={data}
                                          tNamespace={this.tNamespace}
                                          onClose={this.handleCloseDialog}
                                          onConfirm={this.handleConfirmDialog}/>
            );
        }
        return super.renderDefaultDialogs();
    }

    renderHeader(): React.ReactElement {
        return (<>
            <ReadOnlyList title={this.title}
                          data={getValue(this.headerData ?? [])}
            />
            {this.secondaryTitle &&
                <TitleStyled>{this.secondaryTitle}</TitleStyled>}
        </>);
    }

    renderBusyIndicator(): React.ReactElement {
        if (this.props.busyIndicator) {
            return this.props.busyIndicator;
        }
        return null;
    }

    render() {
        return (<>
            <View hotspotContextId={this._hotspotContextId ?? `${this.props.storage.id}Selection`}>
                {this.renderHeader()}
                {this.renderFilterBar()}
                {this.renderAlert()}
                {this.renderTabs()}
                {this.renderToolbar()}
                <TableWrapper>
                    <SmartTable
                        {...this.getTableSharedProps()}
                        initialActiveRows={this._selectedItems}
                        keepActiveRowsOnFilterChange={this._keepActiveRowsOnFilterChange}
                    />
                </TableWrapper>
                {this.renderDefaultDialogs()}
                {this.renderBusyIndicator()}
            </View>
        </>);
    }
}